import React, { useCallback, useEffect, useRef } from "react";
import { useStudentInputStore } from "../../store/useStudentInputStore";
import { useShallow } from "zustand/react/shallow";
import { LatexKeyboard } from "@ever-tutor/et-ui";
import type { KeyDefinition, KeyboardType } from "@ever-tutor/et-ui";
import { mapKeyToInsertion } from "@ever-tutor/et-ui";
import { hitTestCursorPosition } from "../../utils/cursorHitTest";
import { renderMath } from "@/utils/mathjax";
import { noTranslateProps } from "@/utils/pageTranslation";

interface CustomKeyboardProps {
  inputPosition: {
    top: number;
    left: number;
    width: number;
    height: number;
  } | null;
}

const CustomKeyboard: React.FC<CustomKeyboardProps> = React.memo(
  ({ inputPosition }) => {
    const { isKeyboardOpen, activeInputId, activeInputIsMath } =
      useStudentInputStore(
        useShallow((state) => ({
          isKeyboardOpen: state.isKeyboardOpen,
          activeInputId: state.activeInputId,
          activeInputIsMath: state.activeInputIsMath,
        })),
      );

    const closeKeyboard = useStudentInputStore((state) => state.closeKeyboard);
    const insertAtCursor = useStudentInputStore(
      (state) => state.insertAtCursor,
    );
    const backspaceAtCursor = useStudentInputStore(
      (state) => state.backspaceAtCursor,
    );
    const moveCursor = useStudentInputStore((state) => state.moveCursor);
    const tabNavigation = useStudentInputStore((state) => state.tabNavigation);
    const getLatexString = useStudentInputStore(
      (state) => state.getLatexString,
    );

    const syncToDOM = useCallback(
      (inputKey: string) => {
        const latexString = getLatexString(inputKey);
        const domInput = document.querySelector(
          `input[data-input-key="${inputKey}"]`,
        ) as HTMLInputElement;
        if (domInput) {
          domInput.value = latexString;
        }
      },
      [getLatexString],
    );

    /**
     * Physical keyboard handler — shadowed. RevealRenderer binds keydown on
     * the <input> and stops propagation, so nothing here runs while a field
     * has focus; only the dev overlay's keyboard toggle reaches it.
     * The live handler is there — change both or neither.
     */
    useEffect(() => {
      if (!isKeyboardOpen || !activeInputId) return;

      const handlePhysicalKeyDown = (e: KeyboardEvent) => {
        const target = e.target as HTMLElement;
        if (
          target.tagName === "INPUT" &&
          !target.hasAttribute("data-input-key")
        ) {
          return;
        }

        // Undo/redo
        if ((e.ctrlKey || e.metaKey) && e.key === "z" && !e.shiftKey) {
          e.preventDefault();
          const store = useStudentInputStore.getState();
          store.undo?.(activeInputId);
          syncToDOM(activeInputId);
          return;
        }
        if (
          (e.ctrlKey || e.metaKey) &&
          (e.key === "y" || (e.key === "z" && e.shiftKey))
        ) {
          e.preventDefault();
          const store = useStudentInputStore.getState();
          store.redo?.(activeInputId);
          syncToDOM(activeInputId);
          return;
        }

        if (e.key === "Backspace") {
          e.preventDefault();
          backspaceAtCursor(activeInputId);
          syncToDOM(activeInputId);
          return;
        }
        if (e.key === "ArrowLeft") {
          e.preventDefault();
          moveCursor(activeInputId, "left");
          return;
        }
        if (e.key === "ArrowRight") {
          e.preventDefault();
          moveCursor(activeInputId, "right");
          return;
        }
        if (e.key === "ArrowUp") {
          e.preventDefault();
          moveCursor(activeInputId, "up");
          return;
        }
        if (e.key === "ArrowDown") {
          e.preventDefault();
          moveCursor(activeInputId, "down");
          return;
        }
        if (e.key === "Tab") {
          e.preventDefault();
          tabNavigation(activeInputId);
          syncToDOM(activeInputId);
          return;
        }
        if (e.key === "Escape" || e.key === "Enter") {
          e.preventDefault();
          closeKeyboard();
          return;
        }

        if (e.key.length === 1 && !e.ctrlKey && !e.metaKey) {
          e.preventDefault();
          const insertion = mapKeyToInsertion(e.key);
          if (insertion) {
            insertAtCursor(activeInputId, insertion);
            syncToDOM(activeInputId);
          }
        }
      };

      window.addEventListener("keydown", handlePhysicalKeyDown);
      return () => window.removeEventListener("keydown", handlePhysicalKeyDown);
    }, [
      isKeyboardOpen,
      activeInputId,
      backspaceAtCursor,
      moveCursor,
      tabNavigation,
      closeKeyboard,
      insertAtCursor,
      syncToDOM,
    ]);

    // Handle on-screen keyboard key press
    const handleKeyPress = useCallback(
      (keyDef: KeyDefinition) => {
        if (!activeInputId) return;

        // Action keys
        if (keyDef.type === "action") {
          switch (keyDef.value) {
            case "BACKSPACE":
              backspaceAtCursor(activeInputId);
              syncToDOM(activeInputId);
              return;
            case "CURSOR_LEFT":
              moveCursor(activeInputId, "left");
              return;
            case "CURSOR_RIGHT":
              moveCursor(activeInputId, "right");
              return;
            case "UNDO": {
              const store = useStudentInputStore.getState();
              store.undo?.(activeInputId);
              syncToDOM(activeInputId);
              return;
            }
            case "REDO": {
              const store = useStudentInputStore.getState();
              store.redo?.(activeInputId);
              syncToDOM(activeInputId);
              return;
            }
            case "CLOSE":
              closeKeyboard();
              return;
          }
        }

        // ^2: insert superscript with "2" then tab out
        if (keyDef.value === "^2") {
          insertAtCursor(activeInputId, { type: "superscript" });
          insertAtCursor(activeInputId, { type: "text", value: "2" });
          tabNavigation(activeInputId);
          syncToDOM(activeInputId);
          return;
        }

        // Normal key
        const insertion = mapKeyToInsertion(keyDef.value);
        if (insertion) {
          insertAtCursor(activeInputId, insertion);
          syncToDOM(activeInputId);
        }
      },
      [
        activeInputId,
        insertAtCursor,
        backspaceAtCursor,
        moveCursor,
        tabNavigation,
        closeKeyboard,
        syncToDOM,
      ],
    );

    // Backdrop interaction: pass through clicks to inputs and check buttons
    const handleBackdropInteraction = useCallback(
      (e: React.MouseEvent | React.TouchEvent) => {
        let clientX: number, clientY: number;
        if ("touches" in e) {
          const touch = e.touches[0];
          clientX = touch.clientX;
          clientY = touch.clientY;
        } else {
          clientX = e.clientX;
          clientY = e.clientY;
        }

        const backdrop = e.currentTarget as HTMLElement;
        backdrop.style.pointerEvents = "none";
        const elementBelow = document.elementFromPoint(
          clientX,
          clientY,
        ) as HTMLElement | null;
        backdrop.style.pointerEvents = "auto";

        // Click on input with data-input-key — reposition cursor
        const clickedInput =
          elementBelow?.tagName === "INPUT" && elementBelow?.dataset?.inputKey
            ? (elementBelow as HTMLInputElement)
            : null;

        if (clickedInput) {
          const inputKey = clickedInput.dataset.inputKey;
          e.preventDefault();
          clickedInput.focus();

          if (inputKey) {
            const latexDisplayEl = document.querySelector(
              `div[data-latex-display="${inputKey}"]`,
            ) as HTMLElement | null;

            if (latexDisplayEl) {
              requestAnimationFrame(() => {
                const state = useStudentInputStore.getState();
                const doc = state.documents[inputKey];
                if (!doc || doc.nodes.length === 0) return;

                const offset = hitTestCursorPosition(
                  clientX,
                  clientY,
                  latexDisplayEl,
                  doc.nodes.length,
                );
                if (offset !== null) {
                  state.setCursorAbsolute(inputKey, {
                    containerId: "root",
                    containerField: "nodes",
                    offset,
                  });
                }
              });
            }
          }
          return;
        }

        // Click on keyboard itself
        const clickedKeyboard = elementBelow?.closest(
          '[data-custom-keyboard="true"]',
        );
        if (clickedKeyboard) return;

        // Click on Check Answer button
        const clickedCheckButton = elementBelow?.closest(
          '[data-check-button="true"]',
        ) as HTMLElement | null;
        if (
          clickedCheckButton &&
          !(clickedCheckButton as HTMLButtonElement).disabled
        ) {
          e.preventDefault();
          closeKeyboard();
          clickedCheckButton.click();
          return;
        }

        // Outside click — close
        e.preventDefault();
        closeKeyboard();
      },
      [closeKeyboard],
    );

    // Scroll forwarding to active input
    const handleBackdropWheel = useCallback(
      (e: React.WheelEvent) => {
        if (!activeInputId) return;
        const greyBox = document.querySelector(
          `div[data-input-key="${activeInputId}"]`,
        );
        if (greyBox) {
          greyBox.scrollTop += e.deltaY;
          greyBox.scrollLeft += e.deltaX;
        }
      },
      [activeInputId],
    );

    if (!isKeyboardOpen) return null;

    const defaultTab: KeyboardType = activeInputIsMath
      ? "frequent"
      : "alphabet";

    return (
      <>
        {/* Backdrop */}
        <div
          className="fixed inset-0"
          style={{ zIndex: 9998, touchAction: "none" }}
          onMouseDown={handleBackdropInteraction}
          onTouchStart={handleBackdropInteraction}
          onWheel={handleBackdropWheel}
        />

        {/* Keyboard */}
        <div
          data-custom-keyboard="true"
          {...noTranslateProps()}
          style={{ zIndex: 9999, position: "relative" }}
        >
          <LatexKeyboard
            inputPosition={inputPosition}
            onKeyPress={handleKeyPress}
            onClose={closeKeyboard}
            defaultTab={defaultTab}
            renderMath={renderMath}
          />
        </div>
      </>
    );
  },
  (prevProps, nextProps) => {
    const prevPos = prevProps.inputPosition;
    const nextPos = nextProps.inputPosition;
    if (prevPos === nextPos) return true;
    if (!prevPos || !nextPos) return prevPos === nextPos;
    return (
      prevPos.top === nextPos.top &&
      prevPos.left === nextPos.left &&
      prevPos.width === nextPos.width &&
      prevPos.height === nextPos.height
    );
  },
);

export default CustomKeyboard;
